import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { createCarpoolRequest } from '../services/api';
import MapLocationPicker from '../components/MapLocationPicker';
import RouteMap from '../components/RouteMap';
import { LoadingCar } from '../components/LoadingCar';
import './NeedCarpool.css';

function NeedCarpool() {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventData, setUserData, showToast, authData, isAuthenticated, authLoading } = useApp();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    tripType: 'both',
    isAnonymous: false,
    goingLocations: [{ id: Date.now(), address: '', lat: null, lng: null, timeType: 'flexible', specificTime: '' }],
    returnLocations: [{ id: Date.now() + 1, address: '', lat: null, lng: null, timeType: 'flexible', specificTime: '' }],
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { returnTo: location.pathname } });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  useEffect(() => {
    if (!eventData) navigate('/');
  }, [eventData, navigate]);
  
  // Pre-fill form with auth data
  useEffect(() => {
    if (authData) {
      setFormData(prev => ({
        ...prev,
        name: authData.name || '',
        phone: authData.phone || '',
        email: authData.email || ''
      }));
    }
  }, [authData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleLocationChange = useCallback((direction, index, field, value) => {
    setFormData(prev => {
      const locationKey = direction === 'going' ? 'goingLocations' : 'returnLocations';
      const newLocations = [...prev[locationKey]];
      // Create a new object for the specific location to avoid mutation
      newLocations[index] = {
        ...newLocations[index],
        [field]: value
      };
      return { ...prev, [locationKey]: newLocations };
    });
  }, []);

  const addLocation = (direction) => {
    const locationKey = direction === 'going' ? 'goingLocations' : 'returnLocations';
    setFormData(prev => ({
      ...prev,
      [locationKey]: [...prev[locationKey], { address: '', lat: null, lng: null, timeType: 'flexible', specificTime: '' }]
    }));
  };

  const removeLocation = (direction, index) => {
    const locationKey = direction === 'going' ? 'goingLocations' : 'returnLocations';
    const newLocations = formData[locationKey].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [locationKey]: newLocations }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.tripType === 'going' || formData.tripType === 'both') {
      const hasValidGoingLocation = formData.goingLocations.some(loc => loc.address.trim());
      if (!hasValidGoingLocation) newErrors.goingLocations = 'At least one going location is required';
    }
    
    if (formData.tripType === 'return' || formData.tripType === 'both') {
      const hasValidReturnLocation = formData.returnLocations.some(loc => loc.address.trim());
      if (!hasValidReturnLocation) newErrors.returnLocations = 'At least one return location is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const locations = [];
      
      if (formData.tripType === 'going' || formData.tripType === 'both') {
        formData.goingLocations.forEach(loc => {
          if (loc.address.trim()) {
            locations.push({
              location_address: loc.address,
              lat: loc.lat,
              lng: loc.lng,
              trip_direction: 'going',
              time_type: loc.timeType,
              specific_time: loc.timeType === 'specific' ? loc.specificTime : null,
            });
          }
        });
      }
      
      if (formData.tripType === 'return' || formData.tripType === 'both') {
        formData.returnLocations.forEach(loc => {
          if (loc.address.trim()) {
            locations.push({
              location_address: loc.address,
              lat: loc.lat,
              lng: loc.lng,
              trip_direction: 'return',
              time_type: loc.timeType,
              specific_time: loc.timeType === 'specific' ? loc.specificTime : null,
            });
          }
        });
      }
      
      const requestData = {
        event_id: eventData.eventId,
        name: authData?.name || formData.name,
        phone: authData?.phone || formData.phone,
        email: authData?.email || formData.email,
        trip_type: formData.tripType,
        is_anonymous: formData.isAnonymous,
        locations: locations,
      };
      
      const response = await createCarpoolRequest(requestData);
      
      setUserData({
        userId: response.user_id,
        requestId: response.request_id,
        role: 'passenger',
        ...requestData,
      });
      
      showToast('Carpool request created successfully!', 'success');
      navigate('/passenger-dashboard');
    } catch (error) {
      showToast(error.message || 'Failed to create carpool request', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!eventData) return null;
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="car-animation">
          <LoadingCar />
        </div>
        <p>Loading...</p>
      </div>
    );
  }
  if (!isAuthenticated) return null;

  const showGoingLocations = formData.tripType === 'going' || formData.tripType === 'both';
  const showReturnLocations = formData.tripType === 'return' || formData.tripType === 'both';

  return (
    <div className="need-carpool-page">
      {/* Header */}
      <header className="need-carpool-header">
        <button className="need-carpool-back-btn" onClick={() => navigate('/role-selection')}>
          <span>←</span> Back
        </button>
        <div className="need-carpool-header-info">
          <span className="need-carpool-badge">🎫 Request a Ride</span>
          <h1>{eventData.eventName}</h1>
        </div>
      </header>

      {/* Main Container */}
      <div className="need-carpool-container">
        {/* Event Destination */}
        <div className="need-carpool-destination">
          <div className="need-carpool-destination-icon">📍</div>
          <div className="need-carpool-destination-text">
            <h3>Event Destination</h3>
            <p>{eventData.eventLocation || 'Location not specified'}</p>
          </div>
        </div>

        {/* User Profile Card */}
        <div className="need-carpool-user-card">
          <div className="need-carpool-user-info">
            <div className="need-carpool-user-avatar">
              {authData?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="need-carpool-user-details">
              <h4>{authData?.name}</h4>
              <p>{authData?.phone} • {authData?.email}</p>
            </div>
          </div>
          <button
            className="need-carpool-edit-profile"
            onClick={() => navigate('/my-account')}
          >
            Edit
          </button>
        </div>

        <form id="need-carpool-form" onSubmit={handleSubmit}>
          {/* Trip Details Section */}
          <section className="need-carpool-form-section">
            <div className="need-carpool-section-header">
              <div className="need-carpool-section-icon">🗺️</div>
              <div className="need-carpool-section-title">
                <h3>Trip Type</h3>
                <p>Choose your trip direction</p>
              </div>
            </div>

            <div className="need-carpool-field">
              <label className="need-carpool-label">
                Trip Type <span className="need-carpool-required">*</span>
              </label>
              <div className="need-carpool-trip-selector">
                <label className="need-carpool-trip-option">
                  <input
                    type="radio"
                    name="tripType"
                    value="going"
                    checked={formData.tripType === 'going'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <span className="option-icon">➡️</span>
                    <span className="option-label">Going Only</span>
                  </div>
                </label>
                <label className="need-carpool-trip-option">
                  <input
                    type="radio"
                    name="tripType"
                    value="return"
                    checked={formData.tripType === 'return'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <span className="option-icon">⬅️</span>
                    <span className="option-label">Return Only</span>
                  </div>
                </label>
                <label className="need-carpool-trip-option">
                  <input
                    type="radio"
                    name="tripType"
                    value="both"
                    checked={formData.tripType === 'both'}
                    onChange={handleInputChange}
                  />
                  <div className="option-content">
                    <span className="option-icon">↔️</span>
                    <span className="option-label">Both Ways</span>
                  </div>
                </label>
              </div>
            </div>
          </section>
          
          {showGoingLocations && (
            <section className="need-carpool-form-section">
              <div className="need-carpool-section-header">
                <div className="need-carpool-section-icon">📍</div>
                <div className="need-carpool-section-title">
                  <h3>Going Locations</h3>
                  <p>Pickup locations for going to the event</p>
                </div>
              </div>

              {formData.goingLocations.map((location, index) => (
                <div key={`going-${location.id || index}`} className="need-carpool-location-card">
                  <div className="need-carpool-location-header">
                    <div className="need-carpool-location-title">
                      <div className="need-carpool-location-number">{index + 1}</div>
                      <span>Location {index + 1}</span>
                    </div>
                    {formData.goingLocations.length > 1 && (
                      <button
                        type="button"
                        className="need-carpool-remove-location"
                        onClick={() => removeLocation('going', index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="need-carpool-field">
                    <MapLocationPicker
                      key={`going-picker-${location.id || index}`}
                      value={location.address || ''}
                      onChange={(value) => {
                        // Find location by ID to ensure we update the correct one
                        const locationId = location.id;
                        setFormData(prev => {
                          const newLocations = prev.goingLocations.map(loc => {
                            if (loc.id === locationId && loc.address !== value) {
                              return { ...loc, address: value };
                            }
                            return loc;
                          });
                          return { ...prev, goingLocations: newLocations };
                        });
                      }}
                      onLocationSelect={(loc) => {
                        if (!loc || !loc.address) return;
                        // Find location by ID to ensure we update the correct one
                        const locationId = location.id;
                        setFormData(prev => {
                          const newLocations = prev.goingLocations.map(locItem => {
                            if (locItem.id === locationId) {
                              return {
                                ...locItem,
                                address: loc.address || '',
                                lat: loc.lat || null,
                                lng: loc.lng || null
                              };
                            }
                            return locItem;
                          });
                          return { ...prev, goingLocations: newLocations };
                        });
                      }}
                      placeholder="Search for pickup location..."
                    />
                  </div>
                  <div className="need-carpool-field">
                    <label className="need-carpool-label">Time Preference</label>
                    <div className="need-carpool-time-options">
                      <label className="need-carpool-time-option">
                        <input
                          type="radio"
                          checked={location.timeType === 'flexible'}
                          onChange={() => handleLocationChange('going', index, 'timeType', 'flexible')}
                        />
                        <div className="time-content">
                          <span>🕐</span>
                          <span>Flexible</span>
                        </div>
                      </label>
                      <label className="need-carpool-time-option">
                        <input
                          type="radio"
                          checked={location.timeType === 'specific'}
                          onChange={() => handleLocationChange('going', index, 'timeType', 'specific')}
                        />
                        <div className="time-content">
                          <span>⏰</span>
                          <span>Specific Time</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  {location.timeType === 'specific' && (
                    <div className="need-carpool-field">
                      <input
                        type="time"
                        className="form-input"
                        value={location.specificTime}
                        onChange={(e) => handleLocationChange('going', index, 'specificTime', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
              {errors.goingLocations && <div className="need-carpool-error">{errors.goingLocations}</div>}
              <button
                type="button"
                className="need-carpool-add-location"
                onClick={() => addLocation('going')}
              >
                <span>+</span>
                <span>Add Another Going Location</span>
              </button>

              {/* Route Preview Map for Going */}
              {formData.goingLocations.some(loc => loc.address && loc.address.trim()) && (
                <div className="need-carpool-route-preview">
                  <h4>🗺️ Route Preview - Going to Event</h4>
                  {eventData.eventLocation ? (
                    <RouteMap
                      origin={formData.goingLocations.find(loc => loc.address && loc.address.trim())?.address}
                      destination={eventData.eventLocation}
                      waypoints={formData.goingLocations
                        .filter(loc => loc.address && loc.address.trim())
                        .slice(1)
                        .map(loc => loc.address)}
                      height="300px"
                    />
                  ) : (
                    <div className="need-carpool-warning">
                      ⚠️ Event location not set. Map will be shown after event destination is configured.
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          
          {showReturnLocations && (
            <section className="need-carpool-form-section">
              <div className="need-carpool-section-header">
                <div className="need-carpool-section-icon">📍</div>
                <div className="need-carpool-section-title">
                  <h3>Return Locations</h3>
                  <p>Pickup locations for returning from the event</p>
                </div>
              </div>

              {formData.returnLocations.map((location, index) => (
                <div key={`return-${location.id || index}`} className="need-carpool-location-card">
                  <div className="need-carpool-location-header">
                    <div className="need-carpool-location-title">
                      <div className="need-carpool-location-number">{index + 1}</div>
                      <span>Location {index + 1}</span>
                    </div>
                    {formData.returnLocations.length > 1 && (
                      <button
                        type="button"
                        className="need-carpool-remove-location"
                        onClick={() => removeLocation('return', index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="need-carpool-field">
                    <MapLocationPicker
                      key={`return-picker-${location.id || index}`}
                      value={location.address || ''}
                      onChange={(value) => {
                        // Find location by ID to ensure we update the correct one
                        const locationId = location.id;
                        setFormData(prev => {
                          const newLocations = prev.returnLocations.map(loc => {
                            if (loc.id === locationId && loc.address !== value) {
                              return { ...loc, address: value };
                            }
                            return loc;
                          });
                          return { ...prev, returnLocations: newLocations };
                        });
                      }}
                      onLocationSelect={(loc) => {
                        if (!loc || !loc.address) return;
                        // Find location by ID to ensure we update the correct one
                        const locationId = location.id;
                        setFormData(prev => {
                          const newLocations = prev.returnLocations.map(locItem => {
                            if (locItem.id === locationId) {
                              return {
                                ...locItem,
                                address: loc.address || '',
                                lat: loc.lat || null,
                                lng: loc.lng || null
                              };
                            }
                            return locItem;
                          });
                          return { ...prev, returnLocations: newLocations };
                        });
                      }}
                      placeholder="Search for pickup location..."
                    />
                  </div>
                  <div className="need-carpool-field">
                    <label className="need-carpool-label">Time Preference</label>
                    <div className="need-carpool-time-options">
                      <label className="need-carpool-time-option">
                        <input
                          type="radio"
                          checked={location.timeType === 'flexible'}
                          onChange={() => handleLocationChange('return', index, 'timeType', 'flexible')}
                        />
                        <div className="time-content">
                          <span>🕐</span>
                          <span>Flexible</span>
                        </div>
                      </label>
                      <label className="need-carpool-time-option">
                        <input
                          type="radio"
                          checked={location.timeType === 'specific'}
                          onChange={() => handleLocationChange('return', index, 'timeType', 'specific')}
                        />
                        <div className="time-content">
                          <span>⏰</span>
                          <span>Specific Time</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  {location.timeType === 'specific' && (
                    <div className="need-carpool-field">
                      <input
                        type="time"
                        className="form-input"
                        value={location.specificTime}
                        onChange={(e) => handleLocationChange('return', index, 'specificTime', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
              {errors.returnLocations && <div className="need-carpool-error">{errors.returnLocations}</div>}
              <button
                type="button"
                className="need-carpool-add-location"
                onClick={() => addLocation('return')}
              >
                <span>+</span>
                <span>Add Another Return Location</span>
              </button>

              {/* Route Preview Map for Return */}
              {formData.returnLocations.some(loc => loc.address && loc.address.trim()) && (
                <div className="need-carpool-route-preview">
                  <h4>🗺️ Route Preview - Returning from Event</h4>
                  {eventData.eventLocation ? (
                    <RouteMap
                      origin={eventData.eventLocation}
                      destination={formData.returnLocations.find(loc => loc.address && loc.address.trim())?.address}
                      waypoints={formData.returnLocations
                        .filter(loc => loc.address && loc.address.trim())
                        .slice(1)
                        .map(loc => loc.address)}
                      height="300px"
                    />
                  ) : (
                    <div className="need-carpool-warning">
                      ⚠️ Event location not set. Map will be shown after event destination is configured.
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          
          {/* Anonymous Mode Toggle */}
          <section className="need-carpool-form-section">
            <div className="need-carpool-toggle-row">
              <div className="need-carpool-toggle-info">
                <h4>🕵️ Anonymous Mode</h4>
                <p>Hide your name and phone from drivers until they approve you</p>
              </div>
              <label className="need-carpool-toggle">
                <input
                  type="checkbox"
                  name="isAnonymous"
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                />
                <span className="need-carpool-toggle-slider"></span>
              </label>
            </div>
          </section>
        </form>
      </div>

      {/* Submit Section */}
      <div className="need-carpool-submit-section">
        <div className="need-carpool-submit-container">
          <button
            type="button"
            className="need-carpool-cancel-btn"
            onClick={() => navigate('/role-selection')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="need-carpool-form"
            className="need-carpool-submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span>🚗</span>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Submit Request</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="offer-ride-loading-overlay">
          <div className="offer-ride-loading-car">
            <div className="offer-ride-car-body"></div>
            <div className="offer-ride-car-wheel front"></div>
            <div className="offer-ride-car-wheel back"></div>
            <div className="offer-ride-loading-road"></div>
          </div>
          <p className="offer-ride-loading-text">Submitting your request...</p>
        </div>
      )}
    </div>
  );
}

export default NeedCarpool;
